import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Druido L2-5 (manuale V1, rr. 3116-3234). 16 mosse.
 *
 * Pattern notabili:
 * - **Cross-class restriction `prerequisites.anyOf`**: *Colui che Parla
 *   alle Cose* richiede *Nato dalla Terra* OR *Portavoce dell'Aldilà*
 *   (Sciamano — forward-reference).
 * - **3 mosse richiedono *Mutaforma*** (Cambiare Pelle, Artigiano di
 *   Forme, Furia di Denti e Artigli).
 * - **2 mosse richiedono *Nato dalla Terra*** (I Sentieri degli Gnomi, Il
 *   Regno Fatato).
 * - **`Plasmare gli Elementi`**: richiede *Lanciare Incantesimi (Arcani,
 *   Divini o del Patrono)*. Modellata come `prerequisites.anyOf` con
 *   forward-reference cross-class (`mago.lanciare-incantesimi-arcani` OR
 *   `chierico.lanciare-incantesimi-divini` — il Druido nativamente avrebbe
 *   `druido.divinita-della-natura` come trigger; la mossa è semantica
 *   "qualunque casting").
 * - **Mossa multiclasse**: *La Canzone delle Fate* concede *Parole di
 *   Potere* del Bardo. *Fratello del Cacciatore* (multiclasse).
 *
 * **`In Forma e Spirito`** (manuale r. 3120): "Se hai già *Divinità della
 * Natura*, prendi *Mutaforma*, e viceversa". Pattern simile a *Formazione
 * Eterogenea* del Mago. Niente prerequisite strutturato (la mossa è
 * descrittiva e si auto-applica).
 */

const inFormaESpirito: Move = {
  id: 'druido.in-forma-e-spirito',
  name: 'In Forma e Spirito',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando prendi questa mossa al level-up',
  description: 'Se hai già *Divinità della Natura*, prendi *Mutaforma*, e viceversa.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-31 (D5): concede la mossa iniziale rimanente del gruppo del Druido
  // (Divinità della Natura / Mutaforma — pool=1 residuo).
  grantsMoveChoice: { kind: 'from-group', groupId: 'mossa-iniziale-aggiuntiva' },
};

const occhiDellaTigre: Move = {
  id: 'druido.occhi-della-tigre',
  name: 'Occhi della Tigre',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando marchi un animale (con fango, terra o sangue)',
  description:
    'Puoi vedere attraverso i suoi occhi come se fossero i tuoi, non importa la distanza che vi separa. Solo un animale alla volta può essere marchiato in questo modo.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const comunioneDiSussurri: Move = {
  id: 'druido.comunione-di-sussurri',
  name: 'Comunione di Sussurri',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando passi del tempo in un luogo e invochi gli spiriti della natura che vi risiedono',
  description:
    'Tira+**SAG**. Ricevi una visione significativa per te, per i tuoi alleati e per gli spiriti che ti circondano. Con un 10+, la visione è chiara e utile. Con un 7–9, la visione è torbida, e il suo significato confuso. Con un fallimento, la visione è inquietante, spaventosa o traumatizzante, e prendi -1 al prossimo tiro.',
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Visione chiara e utile.' },
    on7to9: { text: 'Visione torbida, significato confuso.' },
    on6minus: { text: 'Visione inquietante; -1 al prossimo tiro.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const cambiarePelle: Move = {
  id: 'druido.cambiare-pelle',
  name: 'Cambiare Pelle',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando prendi danno mentre sei trasformato',
  description:
    'Puoi decidere di spendere tutte le tue prese residue di *Mutaforma* per annullarlo, tornando però così al tuo aspetto normale.',
  tags: [],
  prerequisites: ['druido.mutaforma'],
  replaces: [],
};

const artigianoDiForme: Move = {
  id: 'druido.artigiano-di-forme',
  name: 'Artigiano di Forme',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando usi *Mutaforma*',
  description:
    "Scegli due Caratteristiche. Finché rimani trasformato, prendi +1 continuato ai tiri con una e -1 all'altra.",
  tags: [],
  prerequisites: ['druido.mutaforma'],
  replaces: [],
};

const furiaDiDentiEArtigli: Move = {
  id: 'druido.furia-di-denti-e-artigli',
  name: 'Furia di Denti e Artigli',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando ti trovi in una forma appropriata (qualcosa di pericoloso)',
  description:
    'Aumenta di due categorie il tuo dado di Danno (ad esempio da d6 a d10). Se la categoria dovesse diventare superiore a d12, il dado rimane il d12, ma infliggi +1 danni per ogni aumento di categoria successiva a cui devi rinunciare.\n\n**NB**: il *d12* compare nel manuale **solo** come tetto della progressione di questa mossa, mai come dado di Danno base di una classe (vedi `DamageDieSchema`).',
  tags: [],
  prerequisites: ['druido.mutaforma'],
  replaces: [],
};

const plasmareGliElementi: Move = {
  id: 'druido.plasmare-gli-elementi',
  name: 'Plasmare gli Elementi',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger:
    'Quando ti concentri per qualche minuto plasmando le forze elementali in un incantesimo (o "al volo")',
  description:
    "Spiega l'effetto che desideri (e i suoi limiti), e concorda con il GM il suo livello (0, 1, 3, 5, 7 o 9) usando come spunto gli incantesimi esistenti. Se il tuo livello da Incantatore è più basso di quello concordato, *non* ottieni l'effetto. Sennò, scegli dalla lista un numero di condizioni pari a 1 più la metà del livello stabilito (arrotondando per eccesso). Se non indicato diversamente, puoi anche scegliere la stessa opzione più volte:\n\n- spendi 1 presa di *Mana*;\n- subisci una Debilitazione a tua scelta (puoi scegliere questa opzione una volta per ogni Debilitazione che non hai segnato);\n- impieghi alcune ore (puoi scegliere questa opzione una sola volta);\n- attingi alle energie di un luogo di potere elementale in cui ti trovi (puoi scegliere questa opzione una sola volta);\n- ti fai aiutare da qualcuno (descrivi da chi e come);\n- rinunci a controllare le forze che stai plasmando (il GM ti dirà con quali effetti collaterali);\n- la manipolazione turba l'essenza stessa della natura (il GM ti dirà cosa succede).\n\nSe il GM approva le tue scelte, ottieni l'effetto desiderato. Se l'effetto è continuato, subisci -1 a *lanciare incantesimi* finché resta attivo. Con questa mossa *non* puoi ottenere effetti permanenti.\n\nQuando **manipoli gli elementi 'al volo'**, ti basta qualche secondo. Individua il livello dell'effetto e scegli le condizioni, poi tira come per *lanciare incantesimi*. ✴Con 10+, tutto funziona al meglio. ✴Con 7-9, il GM ti dirà un effetto collaterale, oppure come la magia ha turbato l'essenza della natura o è sfuggita al controllo.",
  tags: [],
  prerequisites: [
    {
      anyOf: [
        'mago.lanciare-incantesimi-arcani',
        'chierico.lanciare-incantesimi-divini',
        'druido.divinita-della-natura',
      ],
    },
  ],
  replaces: [],
};

const coluiCheParlaAlleCose: Move = {
  id: 'druido.colui-che-parla-alle-cose',
  name: 'Colui che Parla alle Cose',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando percepisci spiriti residenti in piante, mare, pietra o specifici luoghi',
  description:
    "Il GM ne terrà conto quando descrive ciò che ti circonda. Puoi ora applicare *Lingua degli Spiriti*, *Mutaforma*, *Studiare l'Essenza* e *Portavoce dell'Aldilà* (Sciamano) anche a tali spiriti. La forma che assumi con *Mutaforma* può essere la copia esatta di un oggetto naturale inanimato, oppure quella di un'entità mobile vegetale o minerale di aspetto vagamente umanoide.",
  tags: [],
  prerequisites: [
    {
      anyOf: ['druido.nato-dalla-terra', 'sciamano.portavoce-dell-aldila'],
    },
  ],
  replaces: [],
};

const pelleCoriacea: Move = {
  id: 'druido.pelle-coriacea',
  name: 'Pelle Coriacea',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando i tuoi piedi toccano il terreno',
  description: 'Hai +1 Armatura.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const supportoElementale: Move = {
  id: 'druido.supporto-elementale',
  name: 'Supporto Elementale',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger:
    "Quando evochi uno spirito primordiale del fuoco, dell'acqua, della terra o dell'aria, affinché svolga un compito per te",
  description:
    "Tira+**SAG**. Con 7+, si materializza un elementale che seguirà *senza discutere* i tuoi ordini al meglio delle sue possibilità. Con 10+, scegli 4 peculiarità dalla lista; con 7-9, scegli 2.\n\nIl GM ti dirà qual è lo specifico Costo dell'elementale e come si concretizzano le eventuali capacità utili che hai descritto. Il gregario rimane al tuo servizio finché non lo congedi o esaurisce i propri PF. Se non paghi il suo Costo prima che l'evocazione si concluda, lo spirito diventa selvaggio e ne perdi il controllo. Puoi avere il supporto di un solo elementale per volta.",
  roll: {
    stat: 'saggezza',
    on10plus: { text: "Si materializza l'elementale; scegli 4 opzioni dalla lista." },
    on7to9: { text: "Si materializza l'elementale; scegli 2 opzioni dalla lista." },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
  // D8a — FI-8c: mossa-source di Follower (Supporto Elementale).
  followerTemplateId: 'druido-supporto-elementale',
  choiceGroups: [
    {
      id: 'elemento',
      label: 'Elemento',
      description: 'Scegli la natura elementale del Supporto evocato.',
      pickCount: 1,
      options: [
        { id: 'fuoco', label: 'Fuoco' },
        { id: 'acqua', label: 'Acqua' },
        { id: 'terra', label: 'Terra' },
        { id: 'aria', label: 'Aria' },
      ],
    },
    {
      id: 'main',
      label: 'Peculiarità',
      description: "Numero di scelte dipende dall'esito del tiro: 4 con 10+, 2 con 7-9.",
      // pickCount statico = 4 (best case 10+). Sovrascritto runtime via
      // `MoveRef.recruitOutcome` → `getPickCountForOutcome` (D8a).
      pickCount: 4,
      options: [
        {
          id: 'potente',
          label: 'Potente',
          description:
            'Un modificatore di Caratteristica a +2 invece di +1 (a +3 se scegli questa opzione 2 volte).',
          allowDuplicate: true,
        },
        {
          id: 'resistente',
          label: 'Resistente',
          description: '+2 PF per ogni tuo livello (al massimo +20 PF).',
        },
        {
          id: 'ben-disposto',
          label: 'Ben disposto',
          description: 'Non devi pagare il suo Costo.',
        },
        {
          id: 'tiene-istinti',
          label: 'Tiene sotto controllo gli istinti',
          description: 'Tiene a freno i suoi istinti primordiali mentre esegue i tuoi ordini.',
        },
        {
          id: 'capacita-utile',
          label: 'Capacità utile',
          description:
            "Descrivi una capacità utile legata alla natura dell'elementale (puoi scegliere fino a 2 volte, ottenendo 2 capacità separate).",
          allowDuplicate: true,
        },
      ],
    },
  ],
  narrativeFields: [
    {
      id: 'nome-elementale',
      label: "Nome dell'elementale",
      placeholder: 'es. Pyros, Aquilon, Petrachus…',
      required: true,
      multiline: false,
    },
    {
      id: 'quale-mod-potente',
      label: 'Modificatore "potente" (+2; +3 se scegli "potente" due volte)',
      placeholder: 'Quale stat ottiene il bonus?',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'main', optionId: 'potente' },
      oneOf: ['forza', 'destrezza', 'costituzione', 'intelligenza', 'saggezza', 'carisma'],
    },
    {
      id: 'capacita-utile-label-1',
      label: 'Capacità utile (1) — titolo',
      placeholder: 'es. Ardente: brucia chiunque tocchi',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'main', optionId: 'capacita-utile', minOccurrences: 1 },
    },
    {
      id: 'capacita-utile-desc-1',
      label: 'Capacità utile (1) — descrizione',
      placeholder: 'Effetto narrativo o meccanico, concordato con il GM.',
      required: false,
      multiline: true,
      requiresChoice: { groupId: 'main', optionId: 'capacita-utile', minOccurrences: 1 },
    },
    {
      id: 'capacita-utile-label-2',
      label: 'Capacità utile (2) — titolo',
      placeholder: 'es. Volo: si sposta liberamente in aria',
      required: true,
      multiline: false,
      requiresChoice: { groupId: 'main', optionId: 'capacita-utile', minOccurrences: 2 },
    },
    {
      id: 'capacita-utile-desc-2',
      label: 'Capacità utile (2) — descrizione',
      placeholder: 'Effetto narrativo o meccanico, concordato con il GM.',
      required: false,
      multiline: true,
      requiresChoice: { groupId: 'main', optionId: 'capacita-utile', minOccurrences: 2 },
    },
  ],
};

const iSentieriDegliGnomi: Move = {
  id: 'druido.i-sentieri-degli-gnomi',
  name: 'I Sentieri degli Gnomi',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger:
    'Quando sei in una delle tue Terre, e cerchi la via più sicura e veloce per condurre il gruppo in un altro posto nella stessa Terra',
  description:
    'Tira+**SAG**. Con 10+, scegli 2. Con 7-9, scegli 1 dalla lista:\n\n- ci arrivate in un lampo;\n- arrivate esattamente dove volevi;\n- non attraversate territori ostili.\n\nCon 6-, vi siete persi.',
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Scegli 2 opzioni dalla lista.' },
    on7to9: { text: 'Scegli 1 opzione dalla lista.' },
    on6minus: { text: 'Vi siete persi.' },
  },
  tags: [],
  prerequisites: ['druido.nato-dalla-terra'],
  replaces: [],
};

const ilRegnoFatato: Move = {
  id: 'druido.il-regno-fatato',
  name: 'Il Regno Fatato',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Hai sviluppato un forte legame con le creature che vivono a cavallo tra questo mondo e il regno fatato. Aggiungi «*il Regno Fatato*» alle tue Terre e scegli un segno rivelatore legato a esso, in aggiunta a quello della tua prima Terra. Ora puoi applicare *Lingua degli Spiriti* a tutte le creature fatate e *Mutaforma* agli animali fatati. Inoltre, hai effimere visioni di luoghi, creature e oggetti che esistono a cavallo tra il regno fatato e questo mondo: il GM ne terrà conto quando descrive ciò che ti circonda.',
  tags: [],
  prerequisites: ['druido.nato-dalla-terra'],
  replaces: [],
};

const laCanzoneDelleFate: Move = {
  id: 'druido.la-canzone-delle-fate',
  name: 'La Canzone delle Fate',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Ottieni la mossa *Parole di Potere* del Bardo (pagina 92). Inoltre, aggiungi il Bardo alle classi da cui puoi prendere mosse con *Fratello del Cacciatore* o *Sorella del Predatore*.',
  tags: ['multiclasse'],
  prerequisites: ['druido.il-regno-fatato'],
  replaces: [],
  // **D7-bundle (FI-17 follow-up)**: bundle deterministico verso
  // `bardo.parole-di-potere`. Acquisita automaticamente quando il PG
  // Druido prende *La Canzone delle Fate* come mossa advanced
  // (acquisition `source: 'main'`, non multiclasse). Manuale V1 r. 3137:
  // «Ottieni la mossa *Parole di Potere* del Bardo».
  // L'estensione pool su *Fratello del Cacciatore* / *Sorella del Predatore*
  // (via `extendsByMove`) lavora in parallelo, basata su `character.moves`.
  bundles: ['bardo.parole-di-potere'],
};

const equilibrio: Move = {
  id: 'druido.equilibrio',
  name: 'Equilibrio',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: "Quando hai qualche minuto di tranquillità per contemplare l'armonia della natura",
  description:
    "Perdi tutte le prese di *Equilibrio* che ti sono rimaste, e tira+**SAG**. Con 10+, ottieni 2 prese. Con 7-9, invece, ne prendi una sola. Puoi usare le tue prese per:\n\n- lenire il dolore e guarire te stesso o un alleato che stai toccando di 1d8 PF per ogni presa utilizzata;\n- curare una malattia o un veleno non magici (li mitighi per un po' di tempo con 1 presa o li guarisci definitivamente con 2).\n\nCon 6-, non ottieni prese e non potrai usare di nuovo questa mossa finché non riesci a concederti un'intera notte di riposo.",
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Ottieni 2 prese di Equilibrio.' },
    on7to9: { text: 'Ottieni 1 presa di Equilibrio.' },
    on6minus: { text: 'Niente prese; non puoi usarla di nuovo fino al riposo.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const tranquillita: Move = {
  id: 'druido.tranquillita',
  name: 'Tranquillità',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando usi *Equilibrio*',
  description:
    'Aggiungi le seguenti opzioni alla lista della mossa *Equilibrio*:\n\n- ridurre di 1 il costo in *Mana* per un risultato di 7-9 in *lanciare incantesimi*. Puoi spendere al massimo una presa per lancio;\n- evitare di spendere una presa di *Mutaforma*, spendendo invece una presa di *Equilibrio*.',
  tags: [],
  prerequisites: ['druido.equilibrio'],
  replaces: [],
};

const fratelloDelCacciatore: Move = {
  id: 'druido.fratello-del-cacciatore',
  name: 'Fratello del Cacciatore',
  type: 'advanced_2_5',
  classId: 'druido',
  trigger: 'Quando prendi questa mossa al level-up',
  description: 'Scegli una mossa non multiclasse del Chierico o del Ranger.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool Chierico/Ranger + Bardo se *La Canzone delle Fate* posseduta.
  // FI-43 (2026-05-15): + Sciamano se archetipo Nano (manuale V1 r. 3041).
  multiclass: {
    picks: { kind: 'fixed-classes', classes: ['chierico', 'ranger'] },
    extendsByArchetype: [{ archetypeId: 'nano', extraClasses: ['sciamano'] }],
    extendsByMove: [{ moveId: 'druido.la-canzone-delle-fate', extraClasses: ['bardo'] }],
  },
};

export const druidoAdvanced2to5Moves: readonly Move[] = [
  inFormaESpirito,
  occhiDellaTigre,
  comunioneDiSussurri,
  cambiarePelle,
  artigianoDiForme,
  furiaDiDentiEArtigli,
  plasmareGliElementi,
  coluiCheParlaAlleCose,
  pelleCoriacea,
  supportoElementale,
  iSentieriDegliGnomi,
  ilRegnoFatato,
  laCanzoneDelleFate,
  equilibrio,
  tranquillita,
  fratelloDelCacciatore,
] as const;
